// frontend/src/pages/dashboard/TaskComponents/TaskTable.js
import React, { memo, Fragment, useState } from "react";
import {
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Typography,
  Box,
  Chip,
  Avatar,
  Tooltip,
  IconButton,
  Badge,
  Button,
  useMediaQuery,
  useTheme,
  Collapse,
  Divider
} from "@mui/material";
import {
  Error as ErrorIcon,
  AttachFile as AttachFileIcon,
  ChatBubbleOutline as ChatBubbleOutlineIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  MoreVert as MoreVertIcon,
  Sort as SortIcon,
  History as HistoryIcon,
  ArrowUpward,
  ArrowDownward,
} from "@mui/icons-material";
import moment from "moment-timezone";
import { convertToCompanyTime } from "../../../utils/taskHelpers";
import { getAvatarUrl } from "../../../utils/imageHelpers";
import TaskCommentSection from "./TaskCommentSection";
import TaskHistoryLog from "./TaskHistoryLog";

const TaskTable = memo(
  ({
    tasks = [],
    handlers,
    authUser,
    hasPermission,
    theme: parentTheme,
    company,
    newCommentText,
    setNewCommentText,
    commentFiles,
    setCommentFiles,
    taskComments,
    commentLoading,
    handleDownload,
    handleImagePreview,
    expandedTaskId,
    expandedSection,
    sprints,
    sortConfig,
    onSort,  
  }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("md"));
    const isSmallDesktop = useMediaQuery(theme.breakpoints.between("md", "lg"));
    const [anchorEl, setAnchorEl] = useState(null);
    const [menuOpenTaskId, setMenuOpenTaskId] = useState(null);

    const columnVisibility = {
      index: true,
      title: true,
      project: !isSmallDesktop,
      sprint: !isSmallDesktop,
      dueDate: !isMobile,
      trackedTime: !isSmallDesktop,
      priority: true,
      status: true,
      assigned: !isSmallDesktop,
      actions: true,
    };

    const totalVisibleColumns = Object.values(columnVisibility).filter(Boolean).length;

    const handleActionMenuOpen = (event, taskId) => {
      event.stopPropagation();
      setAnchorEl(event.currentTarget);
      setMenuOpenTaskId(taskId);
    };

    const handleActionMenuClose = () => {
      setAnchorEl(null);
      setMenuOpenTaskId(null);
    };

    const getPriorityColor = (priority) => {
      switch (priority) {
        case 'high':
        case 'urgent': return 'error';
        case 'low': return 'success';
        default: return 'warning';
      }
    };

    // Simple text truncation WITHOUT Tooltip (to avoid nested tooltips)
    const TruncatedText = ({ text, maxLength = 30, variant = "caption", ...props }) => {
      const displayText = text?.length > maxLength ? `${text.slice(0, maxLength)}...` : text;
      return (
        <Typography
          variant={variant}
          component="span"
          noWrap
          {...props}
        >
          {displayText || "—"}
        </Typography>
      );
    };

    const headerCellStyle = {
      fontWeight: 600,
      fontSize: { xs: "0.65rem", md: "0.75rem", lg: "0.8rem" },
      color: "text.secondary",
      textTransform: "uppercase",
      letterSpacing: "0.3px",
      py: { xs: 1, md: 1.5 },
      px: 1,
      borderBottom: `2px solid ${theme.palette.divider}`,
      boxSizing: 'border-box',
      whiteSpace: 'normal',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      lineHeight: 1.2
    };

    // Column width configuration
    const columnWidths = {
      index: { xs: 40, sm: 50, md: 60 },
      title: { min: { xs: 180, md: 220 }, max: 280 },
      project: { min: 100, max: 140, width: 120 },
      sprint: { min: 90, max: 120, width: 100 },
      dueDate: { min: 100, max: 120, width: 110 },
      trackedTime: { min: 85, max: 100, width: 90 },
      priority: { min: 75, max: 90, width: 80 },
      status: { min: 85, max: 110, width: 100 },
      assigned: { min: 100, max: 140, width: 120 },
      actions: { min: isSmallDesktop ? 90 : 150, max: isSmallDesktop ? 90 : 170, width: isSmallDesktop ? 90 : 150 },
    };

    // Helper to get sprint name
    const getSprintName = (sprintId) => {
      if (!sprintId) return null;
      const sprint = sprints?.find((s) => String(s.id) === String(sprintId));
      return sprint?.name || null;
    };

     // ✅ NEW: Get sort icon based on current sort state
    const getSortIcon = (field) => {
      if (!sortConfig || sortConfig.field !== field) {
        return <SortIcon fontSize="inherit" sx={{ fontSize: "0.875rem", opacity: 0.3 }} />;
      }
      return sortConfig.direction === 'asc' 
        ? <ArrowUpward fontSize="inherit" sx={{ fontSize: "0.875rem", opacity: 0.8, color: 'primary.main' }} />
        : <ArrowDownward fontSize="inherit" sx={{ fontSize: "0.875rem", opacity: 0.8, color: 'primary.main' }} />;
    };

    // ✅ NEW: Sortable header cell style
    const sortableHeaderStyle = (field) => ({
      ...headerCellStyle,
      cursor: 'pointer',
      '&:hover': { 
        backgroundColor: 'action.hover',
        '& .sort-icon': {
          opacity: 0.8,
          color: 'primary.main'
        }
      }
    });

    return (
      <Table sx={{ tableLayout: 'fixed', width: '100%' }}>
        <TableHead>
          <TableRow
            sx={{
              backgroundColor: theme.palette.grey[50],
              '&:hover': { backgroundColor: theme.palette.grey[50] }
            }}
          >
            {columnVisibility.index && (
              <TableCell
                align="center"
                sx={{
                  ...headerCellStyle,
                  width: columnWidths.index.md,
                  minWidth: columnWidths.index.md,
                  maxWidth: columnWidths.index.md,
                }}
              >
                #
              </TableCell>
            )}
            {columnVisibility.title && (
              <TableCell
                sx={{
                    ...sortableHeaderStyle('title'),
                    minWidth: columnWidths.title.min.md,
                    maxWidth: columnWidths.title.max,
                    width: columnWidths.title.max,
                }}
                onClick={() => onSort('title')}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  Task
                  <Box className="sort-icon">{getSortIcon('title')}</Box>
                </Box>
              </TableCell>
            )}
            {columnVisibility.project && (
              <TableCell
                sx={{
                    ...sortableHeaderStyle('project'),
                    minWidth: columnWidths.project.min,
                    maxWidth: columnWidths.project.max,
                    width: columnWidths.project.width,
                }}
                onClick={() => onSort('project')}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  Project
                  <Box className="sort-icon">{getSortIcon('project')}</Box>
                </Box>
              </TableCell>
            )}
            {columnVisibility.sprint && (
              <TableCell
                sx={{
                  ...headerCellStyle,
                  minWidth: columnWidths.sprint.min,
                  maxWidth: columnWidths.sprint.max,
                  width: columnWidths.sprint.width,
                }}
              >
                Sprint
              </TableCell>
            )}
            {columnVisibility.dueDate && (
              <TableCell
                sx={{
                    ...sortableHeaderStyle('due_date'),
                    minWidth: columnWidths.dueDate.min,
                    maxWidth: columnWidths.dueDate.max,
                    width: columnWidths.dueDate.width,
                }}
                onClick={() => onSort('due_date')}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  Due Date
                  <Box className="sort-icon">{getSortIcon('due_date')}</Box>
                </Box>
              </TableCell>
            )}
            {columnVisibility.trackedTime && (
              <TableCell
                sx={{
                  ...headerCellStyle,
                  minWidth: columnWidths.trackedTime.min,
                  maxWidth: columnWidths.trackedTime.max,
                  width: columnWidths.trackedTime.width,
                }}
              >
                Tracked Time
              </TableCell>
            )}
            {columnVisibility.priority && (
                <TableCell
                    sx={{
                        ...sortableHeaderStyle('priority'),
                        minWidth: columnWidths.priority.min,
                        maxWidth: columnWidths.priority.max,
                        width: columnWidths.priority.width,
                    }}
                    onClick={() => onSort('priority')}
                >
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 0.5 }}>
                      Priority
                      <Box className="sort-icon">{getSortIcon('priority')}</Box>
                    </Box>
                </TableCell>
            )}
            {columnVisibility.status && (
              <TableCell
                sx={{
                  ...headerCellStyle,
                  minWidth: columnWidths.status.min,
                  maxWidth: columnWidths.status.max,
                  width: columnWidths.status.width,
                }}
              >
                Status
              </TableCell>
            )}
            {columnVisibility.assigned && (
              <TableCell
                sx={{
                  ...headerCellStyle,
                  minWidth: columnWidths.assigned.min,
                  maxWidth: columnWidths.assigned.max,
                  width: columnWidths.assigned.width,
                }}
              >
                Assigned
              </TableCell>
            )}
            {columnVisibility.actions && (
              <TableCell
                align="right"
                sx={{
                  ...headerCellStyle,
                  minWidth: columnWidths.actions.min,
                  maxWidth: columnWidths.actions.max,
                  width: columnWidths.actions.width,
                }}
              >
                Actions
              </TableCell>
            )}
          </TableRow>
        </TableHead>
        <TableBody>
          {tasks.map((task, index) => {
            const isExpanded = expandedTaskId === task.id;
            const dueDateLocal = task.due_date
              ? moment.utc(task.due_date).tz(company.timezone)
              : null;
            const isOverdue =
              dueDateLocal &&
              moment() > dueDateLocal &&
              !["completed", "done"].includes(task.statusObj?.key);
            const sprintName = getSprintName(task.sprint_id);

            return (
              <Fragment key={task.id}>
                <TableRow
                  hover
                  onClick={() => handlers.onViewDetails(task)}
                  sx={{
                    cursor: "pointer",
                    "&:hover": { backgroundColor: "action.hover" },
                    '&:last-child td': { borderBottom: 0 },
                    '& .MuiTableCell-root': {
                      boxSizing: 'border-box',
                    }
                  }}
                >
                  {columnVisibility.index && (
                    <TableCell
                      align="center"
                      sx={{
                        width: columnWidths.index.md,
                        minWidth: columnWidths.index.md,
                        maxWidth: columnWidths.index.md,
                        py: 1.5,
                        fontSize: { xs: "0.75rem", md: "0.875rem" },
                        fontWeight: 600,
                        color: "text.secondary",
                        boxSizing: 'border-box'
                      }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      {String(index + 1).padStart(2, "0")}
                    </TableCell>
                  )}
                  {columnVisibility.title && (
                    <TableCell
                      sx={{
                        minWidth: columnWidths.title.min.md,
                        maxWidth: columnWidths.title.max,
                        py: 1.5,
                        px: 1,
                        boxSizing: 'border-box'
                      }}
                    >
                      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                        <Tooltip title={task.title} arrow placement="top">
                          <Typography
                            variant="subtitle2"
                            fontWeight={600}
                            noWrap
                            sx={{
                              fontSize: { xs: "0.8rem", md: "0.875rem" },
                              '&:hover': { color: 'primary.main' }
                            }}
                          >
                            {task.title}
                          </Typography>
                        </Tooltip>
                        {isOverdue && (
                          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                            <ErrorIcon fontSize="small" color="error" sx={{ fontSize: "0.75rem" }} />
                            <Typography variant="caption" color="error" fontWeight={500}>
                              Overdue
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    </TableCell>
                  )}
                  {columnVisibility.project && (
                    <TableCell
                        sx={{
                        minWidth: columnWidths.project.min,
                        maxWidth: columnWidths.project.max,
                        width: columnWidths.project.width,
                        py: 1.5,
                        px: 1,
                        boxSizing: 'border-box'
                        }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <Button
                        size="small"
                        onClick={(e) => {
                            e.stopPropagation();
                            handlers.onViewProject(task.project_id);
                        }}
                        sx={{
                            justifyContent: "flex-start",
                            minWidth: "auto",
                            px: 0.5,
                            fontSize: { xs: "0.7rem", md: "0.75rem" },
                            textTransform: "none",
                            color: 'primary.main',
                            maxWidth: "100%",
                            width: '100%',
                            '&:hover': {
                            backgroundColor: 'transparent',
                            textDecoration: 'underline'
                            }
                        }}
                        >
                        {/* ✅ Tooltip on Typography, not wrapping Button */}
                        <Tooltip 
                            title={task.project?.name || "N/A"} 
                            arrow 
                            placement="top"
                            disableHoverListener={task.project?.name?.length <= 15}
                        >
                            <Typography
                            variant="body2"
                            noWrap
                            sx={{
                                fontSize: { xs: "0.7rem", md: "0.75rem" },
                                maxWidth: "100%",
                                overflow: 'hidden',
                                textOverflow: 'ellipsis'
                            }}
                            >
                            {task.project?.name || "N/A"}
                            </Typography>
                        </Tooltip>
                        </Button>
                    </TableCell>
                )}
                  {columnVisibility.sprint && (
                    <TableCell
                      sx={{
                        minWidth: columnWidths.sprint.min,
                        maxWidth: columnWidths.sprint.max,
                        width: columnWidths.sprint.width,
                        py: 1.5,
                        px: 1,
                        boxSizing: 'border-box'
                      }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      {(() => {
                        // Find sprint once and reuse
                        const sprint = sprints?.find((s) => String(s.id) === String(task.sprint_id));
                        const sprintName = sprint?.name || null;
                        if (!task.sprint_id || !sprintName) {
                          return (
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.7rem" }}>
                              —
                            </Typography>
                          );
                        }
                        // ✅ FIXED: Removed nested Tooltip - Chip has simple text
                        return (
                          <Chip
                            label={sprintName.length > 14 ? `${sprintName.slice(0, 14)}...` : sprintName}
                            size="small"
                            color="primary"
                            variant="outlined"
                            sx={{
                              maxWidth: "100%",
                              height: 22,
                              fontSize: { xs: "0.6rem", md: "0.65rem" },
                              '& .MuiChip-label': {
                                px: 0.75,
                                fontSize: { xs: "0.6rem", md: "0.65rem" }
                              }
                            }}
                          />
                        );
                      })()}
                    </TableCell>
                  )}
                  {columnVisibility.dueDate && (
                    <TableCell
                      sx={{
                        minWidth: columnWidths.dueDate.min,
                        maxWidth: columnWidths.dueDate.max,
                        width: columnWidths.dueDate.width,
                        py: 1.5,
                        px: 1,
                        fontSize: { xs: "0.7rem", md: "0.75rem" },
                        boxSizing: 'border-box'
                      }}
                    >
                      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}>
                        <Typography variant="body2" fontWeight={500}>
                          {task.due_date
                            ? moment(convertToCompanyTime(task.due_date, company.timezone)).format("MMM DD")
                            : "N/A"}
                        </Typography>
                        {task.due_date && (
                          <Typography variant="caption" color="text.secondary">
                            {moment(convertToCompanyTime(task.due_date, company.timezone)).format("h:mm A")}
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                  )}
                  {columnVisibility.trackedTime && (
                    <TableCell
                      sx={{
                        minWidth: columnWidths.trackedTime.min,
                        maxWidth: columnWidths.trackedTime.max,
                        width: columnWidths.trackedTime.width,
                        py: 1.5,
                        px: 1,
                        fontSize: { xs: "0.7rem", md: "0.75rem" },
                        boxSizing: 'border-box'
                      }}
                    >
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Typography variant="body2" fontWeight={500}>
                          {task.tracked_time?.formatted || "00h 00m"}
                        </Typography>
                        {task.tracked_time?.status === "Running" && (
                          <Tooltip title="Timer running" arrow>
                            <Box
                              sx={{
                                width: 8,
                                height: 8,
                                borderRadius: "50%",
                                bgcolor: "success.main",
                                flexShrink: 0
                              }}
                            />
                          </Tooltip>
                        )}
                        {task.tracked_time?.status === "Paused" && (
                          <Tooltip title="Timer paused" arrow>
                            <Box
                              sx={{
                                width: 8,
                                height: 8,
                                borderRadius: "50%",
                                bgcolor: "warning.main",
                                flexShrink: 0
                              }}
                            />
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                  )}
                  {columnVisibility.priority && (
                    <TableCell
                      sx={{
                        minWidth: columnWidths.priority.min,
                        maxWidth: columnWidths.priority.max,
                        width: columnWidths.priority.width,
                        py: 1.5,
                        px: 1,
                        boxSizing: 'border-box'
                      }}
                    >
                      <Chip
                        label={task.priority?.charAt(0).toUpperCase() + task.priority?.slice(1)}
                        color={getPriorityColor(task.priority)}
                        size="small"
                        sx={{
                          fontSize: { xs: "0.65rem", md: "0.7rem" },
                          height: 24,
                          '& .MuiChip-label': { px: 1 }
                        }}
                      />
                    </TableCell>
                  )}
                  {columnVisibility.status && (
                    <TableCell
                      sx={{
                        minWidth: columnWidths.status.min,
                        maxWidth: columnWidths.status.max,
                        width: columnWidths.status.width,
                        py: 1.5,
                        px: 1,
                        boxSizing: 'border-box'
                      }}
                    >
                      <Tooltip title={task.statusObj?.name || "Unknown"} arrow>
                        <Chip
                          label={task.statusObj?.name?.length > 10 
                            ? `${task.statusObj?.name?.slice(0, 10)}...` 
                            : task.statusObj?.name}
                          sx={{
                            backgroundColor: task.statusObj?.color || "#9CA3AF",
                            color: "#fff",
                            fontSize: { xs: "0.65rem", md: "0.7rem" },
                            height: 24,
                            maxWidth: "100%",
                            '& .MuiChip-label': { px: 1, textTransform: 'uppercase' }
                          }}
                          size="small"
                        />
                      </Tooltip>
                    </TableCell>
                  )}
                  {columnVisibility.assigned && (
                    <TableCell
                      sx={{
                        minWidth: columnWidths.assigned.min,
                        maxWidth: columnWidths.assigned.max,
                        width: columnWidths.assigned.width,
                        py: 1.5,
                        px: 1,
                        boxSizing: 'border-box'
                      }}
                    >
                      {task.assignedUsers?.length > 0 ? (
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexWrap: "wrap" }}>
                          {task.assignedUsers.slice(0, isSmallDesktop ? 2 : 3).map((user) => (
                            <Tooltip key={user.id} title={user.name} placement="top" arrow>
                              <Avatar
                                sx={{
                                  width: { xs: 22, md: 24 },
                                  height: { xs: 22, md: 24 },
                                  fontSize: { xs: 10, md: 12 },
                                  border: "2px solid",
                                  borderColor: "background.paper",
                                  cursor: "pointer"
                                }}
                                src={getAvatarUrl(user.avatar)}
                                onError={(e) => {
                                  e.target.onerror = null;
                                  e.target.src = null;
                                }}
                              >
                                {!user.avatar ? user.name?.charAt(0)?.toUpperCase() : null}
                              </Avatar>
                            </Tooltip>
                          ))}
                          {task.assignedUsers.length > (isSmallDesktop ? 2 : 3) && (
                            <Tooltip
                              title={task.assignedUsers.slice(isSmallDesktop ? 2 : 3).map(u => u.name).join(", ")}
                              arrow
                            >
                              <Chip
                                label={`+${task.assignedUsers.length - (isSmallDesktop ? 2 : 3)}`}
                                size="small"
                                variant="outlined"
                                sx={{
                                  height: { xs: 20, md: 22 },
                                  minWidth: 28,
                                  '& .MuiChip-label': {
                                    px: 1,
                                    fontSize: { xs: "0.6rem", md: "0.65rem" }
                                  }
                                }}
                              />
                            </Tooltip>
                          )}
                        </Box>
                      ) : (
                        <Typography variant="caption" color="text.secondary">Unassigned</Typography>
                      )}
                    </TableCell>
                  )}
                  {columnVisibility.actions && (
                    <TableCell
                      align="right"
                      sx={{
                        minWidth: columnWidths.actions.min,
                        maxWidth: columnWidths.actions.max,
                        width: columnWidths.actions.width,
                        py: 1.5,
                        px: 1,
                        boxSizing: 'border-box'
                      }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Box sx={{
                        display: "flex",
                        justifyContent: "flex-end",
                        alignItems: "center",
                        gap: { xs: 0.25, md: 0.5 },
                        flexWrap: isSmallDesktop ? "wrap" : "nowrap",
                        position: 'relative'
                      }}>
                        {isSmallDesktop ? (
                          <>
                            <Tooltip title="Actions" arrow>
                              <IconButton
                                size="small"
                                onClick={(e) => handleActionMenuOpen(e, task.id)}
                                sx={{ p: 0.5 }}
                              >
                                <MoreVertIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            {Boolean(anchorEl) && menuOpenTaskId === task.id && (
                              <Box
                                sx={{
                                  position: "absolute",
                                  right: 0,
                                  top: "100%",
                                  zIndex: 1300,
                                  bgcolor: "background.paper",
                                  borderRadius: 1,
                                  boxShadow: theme.shadows[8],
                                  p: 0.5,
                                  display: "flex",
                                  flexDirection: "column",
                                  gap: 0.25,
                                  minWidth: 140,
                                  border: `1px solid ${theme.palette.divider}`
                                }}
                                onClick={(e) => e.stopPropagation()}
                              >
                                {task.attachments?.length > 0 && (
                                  <Button
                                    size="small"
                                    startIcon={<AttachFileIcon fontSize="small" />}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handlers.onViewAttachments(task.attachments);
                                      handleActionMenuClose();
                                    }}
                                    sx={{
                                      justifyContent: "flex-start",
                                      fontSize: "0.75rem",
                                      minHeight: 32
                                    }}
                                  >
                                    Attachments ({task.attachments.length})
                                  </Button>
                                )}
                                <Button
                                  size="small"
                                  startIcon={<ChatBubbleOutlineIcon fontSize="small" />}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handlers.onToggleComments(task.id);
                                    handleActionMenuClose();
                                  }}
                                  sx={{
                                    justifyContent: "flex-start",
                                    fontSize: "0.75rem",
                                    minHeight: 32
                                  }}
                                >
                                  Comments ({task.comment_count || 0})
                                </Button>
                                <Button
                                  size="small"
                                  startIcon={<HistoryIcon fontSize="small" />}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handlers.onToggleHistory(task.id);
                                    handleActionMenuClose();
                                  }}
                                  sx={{
                                    justifyContent: "flex-start",
                                    fontSize: "0.75rem",
                                    minHeight: 32
                                  }}
                                >
                                  History ({task.history_count || 0})
                                </Button>
                                {(hasPermission("task.update") ||
                                  hasPermission("task.manage.team") ||
                                  task.assignedUsers?.some((u) => u.id === authUser?.id)) && (
                                  <Button
                                    size="small"
                                    startIcon={<EditIcon fontSize="small" />}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handlers.onEdit(task);
                                      handleActionMenuClose();
                                    }}
                                    sx={{
                                      justifyContent: "flex-start",
                                      fontSize: "0.75rem",
                                      color: 'primary.main',
                                      minHeight: 32
                                    }}
                                  >
                                    Edit
                                  </Button>
                                )}
                                {hasPermission("task.delete") && (
                                  <Button
                                    size="small"
                                    startIcon={<DeleteIcon fontSize="small" />}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handlers.onDelete(task.id);
                                      handleActionMenuClose();
                                    }}
                                    sx={{
                                      justifyContent: "flex-start",
                                      fontSize: "0.75rem",
                                      color: 'error.main',
                                      minHeight: 32
                                    }}
                                  >
                                    Delete
                                  </Button>
                                )}
                                <Button
                                  size="small"
                                  startIcon={<VisibilityIcon fontSize="small" />}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handlers.onViewDetails(task);
                                    handleActionMenuClose();
                                  }}
                                  sx={{
                                    justifyContent: "flex-start",
                                    fontSize: "0.75rem",
                                    minHeight: 32
                                  }}
                                >
                                  Details
                                </Button>
                              </Box>
                            )}
                          </>
                        ) : (
                          <Fragment>
                            {task.attachments?.length > 0 && (
                              <Tooltip title={`${task.attachments.length} attachments`} arrow>
                                <Badge
                                  badgeContent={task.attachments.length || 0}
                                  color="primary"
                                  sx={{ '& .MuiBadge-badge': { fontSize: '0.65rem', minWidth: 16, height: 16 } }}
                                >
                                  <IconButton
                                    size="small"
                                    sx={{ p: { xs: 0.3, md: 0.5 } }}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handlers.onViewAttachments(task.attachments);
                                    }}
                                  >
                                    <AttachFileIcon fontSize="small" />
                                  </IconButton>
                                </Badge>
                              </Tooltip>
                            )}
                            <Tooltip title={`${task.comment_count || 0} comments`} arrow>
                              <Badge
                                badgeContent={task.comment_count || 0}
                                color="primary"
                                sx={{ '& .MuiBadge-badge': { fontSize: '0.65rem', minWidth: 16, height: 16 } }}
                              >
                                <IconButton
                                  size="small"
                                  sx={{ p: { xs: 0.3, md: 0.5 } }}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handlers.onToggleComments(task.id);
                                  }}
                                >
                                  <ChatBubbleOutlineIcon fontSize="small" />
                                </IconButton>
                              </Badge>
                            </Tooltip>
                            <Tooltip title={`${task.history_count || 0} history logs`} arrow>
                              <Badge
                                badgeContent={task.history_count || 0}
                                color="secondary"
                                sx={{ '& .MuiBadge-badge': { fontSize: '0.65rem', minWidth: 16, height: 16 } }}
                              >
                                <IconButton
                                  size="small"
                                  sx={{ p: { xs: 0.3, md: 0.5 } }}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handlers.onToggleHistory(task.id);
                                  }}
                                >
                                  <HistoryIcon fontSize="small" />
                                </IconButton>
                              </Badge>
                            </Tooltip>
                            {(hasPermission("task.update") ||
                              hasPermission("task.manage.team") ||
                              task.assignedUsers?.some((u) => u.id === authUser?.id)) && (
                              <Tooltip title="Edit task" arrow>
                                <IconButton
                                  size="small"
                                  sx={{ p: { xs: 0.3, md: 0.5 } }}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handlers.onEdit(task);
                                  }}
                                >
                                  <EditIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                            {hasPermission("task.delete") && (
                              <Tooltip title="Delete task" arrow>
                                <IconButton
                                  size="small"
                                  sx={{ p: { xs: 0.3, md: 0.5 } }}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handlers.onDelete(task.id);
                                  }}
                                  color="error"
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                            <Tooltip title="View details" arrow>
                              <IconButton
                                size="small"
                                sx={{ p: { xs: 0.3, md: 0.5 } }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handlers.onViewDetails(task);
                                }}
                              >
                                <VisibilityIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Fragment>
                        )}
                      </Box>
                    </TableCell>
                  )}
                </TableRow>
                {isExpanded && (
                  <TableRow>
                    <TableCell
                      colSpan={totalVisibleColumns}
                      sx={{
                        p: 0,
                        borderBottom: `1px solid ${theme.palette.divider}`,
                        bgcolor: "action.hover",
                        boxSizing: 'border-box',
                        width: '100%'
                      }}
                    >
                      <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                        <Box sx={{
                          p: { xs: 2, md: 3 },
                          maxHeight: { xs: "400px", md: "600px" },
                          overflowY: "auto",
                          '&::-webkit-scrollbar': { width: 6 },
                          '&::-webkit-scrollbar-thumb': {
                            bgcolor: theme.palette.grey[400],
                            borderRadius: 3
                          }
                        }}>
                          {expandedSection === 'comments' && (
                            <Box>
                              <Box sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                mb: 1
                              }}>
                                <Typography variant="subtitle2" fontWeight={600} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  <ChatBubbleOutlineIcon fontSize="small" />
                                  Comments ({task.comment_count || 0})
                                </Typography>
                              </Box>
                              <TaskCommentSection
                                taskId={task.id}
                                comments={taskComments[task.id] || []}
                                loading={commentLoading[task.id]}
                                onPostComment={handlers.onPostComment}
                                onDeleteComment={handlers.onDeleteComment}
                                users={task.assignedUsers || []}
                                authUser={authUser}
                                newCommentText={newCommentText}
                                setNewCommentText={setNewCommentText}
                                commentFiles={commentFiles}
                                setCommentFiles={setCommentFiles}
                                theme={parentTheme}
                                hasPermission={hasPermission}
                                handleDownload={handleDownload}
                                handleImagePreview={handleImagePreview}
                              />
                            </Box>
                          )}
                          {expandedSection === 'history' && (
                            <Box>
                              <Box sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                mb: 1
                              }}>
                                <Typography variant="subtitle2" fontWeight={600} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  <HistoryIcon fontSize="small" />
                                  Activity Log ({task.history_count || 0})
                                </Typography>
                              </Box>
                              <TaskHistoryLog
                                taskId={task.id}
                                company={company}
                                theme={parentTheme}
                              />
                            </Box>
                          )}
                          {!expandedSection && (
                            <Box>
                              <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
                                Comments ({task.comment_count || 0})
                              </Typography>
                              <TaskCommentSection
                                taskId={task.id}
                                comments={taskComments[task.id] || []}
                                loading={commentLoading[task.id]}
                                onPostComment={handlers.onPostComment}
                                onDeleteComment={handlers.onDeleteComment}
                                users={task.assignedUsers || []}
                                authUser={authUser}
                                newCommentText={newCommentText}
                                setNewCommentText={setNewCommentText}
                                commentFiles={commentFiles}
                                setCommentFiles={setCommentFiles}
                                theme={parentTheme}
                                hasPermission={hasPermission}
                                handleDownload={handleDownload}
                                handleImagePreview={handleImagePreview}
                              />
                            </Box>
                          )}
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                )}
              </Fragment>
            );
          })}
        </TableBody>
      </Table>
    );
  }
);

export default TaskTable;